import { formatCurrencyCompact, formatPercent } from '../../utils/formatters';
import { MetricLabel } from './MetricLabel';

interface DealSnapshotBarProps {
  units: number;
  unitConfidence?: string;
  unitSource?: string;
  capexBudget: number;
  stabilizedNoi: number;
  offerRange?: { low: number; high: number };
  bestIrr?: number | null;
  bestEm?: number | null;
  entryCapRate?: number;
  hasCapex?: boolean;
  verdict?: 'GO' | 'NOGO' | 'CONDITIONAL';
  dataConfidence?: 'high' | 'medium' | 'low';
  leadScore?: number | null;
}

interface MetricProps {
  label: React.ReactNode;
  value: string;
  sublabel?: string;
  accent?: boolean;
}

function Metric({ label, value, sublabel, accent }: MetricProps) {
  return (
    <div className="flex flex-col items-center px-4 py-2 min-w-0">
      <span className="text-sm uppercase tracking-wider text-stone-400 whitespace-nowrap">{label}</span>
      <span className={`text-lg font-mono tabular-nums font-bold ${accent ? 'text-gold' : 'text-ivory'} whitespace-nowrap`}>
        {value}
      </span>
      {sublabel && (
        <span className="text-sm text-stone-400 whitespace-nowrap">{sublabel}</span>
      )}
    </div>
  );
}

function VerdictBadge({ verdict }: { verdict: 'GO' | 'NOGO' | 'CONDITIONAL' }) {
  const config = {
    GO: { bg: 'bg-emerald-500/20', border: 'border-emerald-500/40', text: 'text-emerald-400', label: 'GO' },
    NOGO: { bg: 'bg-red-500/20', border: 'border-red-500/40', text: 'text-red-400', label: 'NO-GO' },
    CONDITIONAL: { bg: 'bg-amber-500/20', border: 'border-amber-500/40', text: 'text-amber-400', label: 'COND' },
  };
  const c = config[verdict];
  return (
    <div className={`${c.bg} ${c.border} border rounded-md px-3 py-1.5 flex items-center gap-1.5`}>
      <div className={`w-2 h-2 rounded-full ${verdict === 'GO' ? 'bg-emerald-400' : verdict === 'NOGO' ? 'bg-red-400' : 'bg-amber-400'}`} />
      <span className={`text-sm font-bold tracking-wide ${c.text}`}>{c.label}</span>
    </div>
  );
}

export function DealSnapshotBar({
  units,
  unitConfidence,
  unitSource,
  capexBudget,
  stabilizedNoi,
  offerRange,
  bestIrr,
  bestEm,
  entryCapRate,
  hasCapex,
  verdict,
  dataConfidence,
  leadScore,
}: DealSnapshotBarProps) {
  const offerRangeStr = offerRange
    ? `${formatCurrencyCompact(offerRange.low)} – ${formatCurrencyCompact(offerRange.high)}`
    : '—';

  const unitSublabel = unitSource
    ? `${units} units (${unitSource})`
    : unitConfidence ? `conf: ${unitConfidence}` : undefined;

  return (
    <div className="flex items-center justify-between bg-charcoal/60 border border-slate-dark rounded-lg overflow-x-auto">
      {verdict && (
        <>
          <div className="px-3 py-2">
            <VerdictBadge verdict={verdict} />
          </div>
          <div className="w-px h-8 bg-slate-dark" />
        </>
      )}
      <div className="relative flex items-center">
        <Metric
          label="Units"
          value={String(units)}
          sublabel={unitSublabel}
        />
        {unitConfidence && (
          <div
            className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${
              unitConfidence === 'HIGH' ? 'bg-emerald-400'
              : unitConfidence === 'MEDIUM' ? 'bg-amber-400'
              : 'bg-red-400'
            }`}
            title={`${unitConfidence} confidence unit count`}
          />
        )}
      </div>
      <div className="w-px h-8 bg-slate-dark" />
      <Metric
        label="CapEx"
        value={formatCurrencyCompact(capexBudget)}
      />
      <div className="w-px h-8 bg-slate-dark" />
      <Metric
        label={<MetricLabel metric="stabilized_noi" className="text-sm uppercase tracking-wider text-stone-400">Stabilized NOI</MetricLabel>}
        value={formatCurrencyCompact(stabilizedNoi)}
      />
      <div className="w-px h-8 bg-slate-dark" />
      <Metric
        label={<MetricLabel metric="entry_cap" className="text-sm uppercase tracking-wider text-stone-400">{hasCapex ? 'Yield on Cost' : 'Entry Cap'}</MetricLabel>}
        value={entryCapRate ? formatPercent(entryCapRate, 2) : '—'}
        sublabel={hasCapex ? 'post-reno' : undefined}
      />
      <div className="w-px h-8 bg-slate-dark" />
      <Metric
        label="Offer Range"
        value={offerRangeStr}
        accent
      />
      {bestIrr != null && bestIrr > 0 && (
        <>
          <div className="w-px h-8 bg-slate-dark" />
          <Metric
            label={<MetricLabel metric="irr" className="text-sm uppercase tracking-wider text-stone-400">Best IRR</MetricLabel>}
            value={formatPercent(bestIrr, 1)}
            accent
          />
        </>
      )}
      {bestEm != null && bestEm > 0 && (
        <>
          <div className="w-px h-8 bg-slate-dark" />
          <Metric
            label={<MetricLabel metric="equity_multiple" className="text-sm uppercase tracking-wider text-stone-400">Best EM</MetricLabel>}
            value={`${bestEm.toFixed(2)}x`}
            accent
          />
        </>
      )}
      {leadScore != null && (
        <>
          <div className="w-px h-8 bg-slate-dark" />
          <div className="flex flex-col items-center px-4 py-2 min-w-0">
            <span className="text-sm uppercase tracking-wider text-stone-400 whitespace-nowrap">Lead Score</span>
            <span className={`text-lg font-mono tabular-nums font-bold whitespace-nowrap ${
              leadScore >= 70 ? 'text-emerald' : leadScore >= 40 ? 'text-amber-400' : 'text-crimson'
            }`}>
              {Math.round(leadScore)}/100
            </span>
          </div>
        </>
      )}
      {dataConfidence === 'low' && (
        <>
          <div className="w-px h-8 bg-slate-dark" />
          <div className="px-3 py-1.5">
            <div className="bg-amber-500/20 border border-amber-500/40 rounded-md px-2.5 py-1 flex items-center gap-1.5">
              <div className="w-1.5 h-1.5 rounded-full bg-amber-400" />
              <span className="text-sm font-bold tracking-wide text-amber-400 uppercase whitespace-nowrap">
                Limited Data
              </span>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
